import { FetchArgs } from '@reduxjs/toolkit/query/react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { datadogRum } from '@/lib/DatadogService';
import { baseQueryType } from '@/resiliency/util';
import { clearExpiredFailures, getResiliencyStorage, incrementFailure } from '@/resiliency/storage';

/**
 * Wraps a base query function with a circuit breaker mechanism to limit the number of failed requests.
 *
 * The circuit breaker tracks failures both per-request (by URL) and in total, using configurable limits
 * from environment variables. If the failure count exceeds the configured limits, further requests are blocked
 * and an error response is returned. Failure counts are incremented for specific error codes, also configurable.
 *
 * @param baseUrl - The base URL to prepend to request URLs.
 * @param baseQuery - The base query function to wrap, typically an RTK Query base query.
 * @returns A new base query function with circuit breaker logic applied.
 *
 * @remarks
 * - Uses environment variables:
 *   - `VITE_CIRCUIT_MAX_FAILURES_PER_REQUEST`: Maximum failures allowed per request URL.
 *   - `VITE_CIRCUIT_MAX_FAILURES_TOTAL`: Maximum total failures allowed.
 *   - `VITE_RESILIENCY_ERROR_STATUSES`: Comma-separated list of error codes that trigger failure counting.
 * - Tracks exceptions using Application Insights.
 * - Clears expired failures before each request.
 */
export const baseQueryWithCircuitBreaker = (
  baseUrl: string,
  baseQuery: baseQueryType
): baseQueryType => {
  const maxPerRequest = parseInt(import.meta.env.VITE_CIRCUIT_MAX_FAILURES_PER_REQUEST ?? '-1');
  const maxTotal = parseInt(import.meta.env.VITE_CIRCUIT_MAX_FAILURES_TOTAL ?? '-1');
  const errorCodesForRetry: (string | number)[] =
    import.meta?.env?.VITE_RESILIENCY_ERROR_STATUSES?.split(',')?.map((a: string) =>
      !isNaN(Number(a)) ? parseInt(a) : a
    ) ?? [];

  const maxRequestTotalBreakerEnabled = maxTotal >= 0;
  const maxRequestBreakerEnabled = maxPerRequest >= 0;

  const BaseQueryWithCircuitBreaker: baseQueryType = async (...args) => {
    const trackException = (msg: string, failure_limit: number, url?: string) =>
      datadogRum.addError(new Error(`[Circuit Breaker Policy] ${msg}`), {
        failure_limit,
        url
      });

    let urlString = '';

    clearExpiredFailures();
    const _args = args[0];
    const path = (_args as FetchArgs)?.url ?? '';

    try {
      // Construct the URL properly by providing base URL as second parameter
      const absoluteBase = baseUrl.startsWith('http')
        ? baseUrl
        : `http://localhost${baseUrl.startsWith('/') ? baseUrl : `/${baseUrl}`}`;
      const fullPath = path.startsWith('/') ? path : `/${path}`;
      const url = new URL(fullPath, absoluteBase);
      urlString = url.toString();
    } catch (ex) {
      // Track the error with context and return early
      trackException(
        `Failed to construct URL: ${(ex as Error).message}`,
        maxPerRequest,
        `baseUrl: ${baseUrl}, path: ${path}`
      );

      // Use PARSING_ERROR status with stringified data and originalStatus
      const error: FetchBaseQueryError = {
        status: 'PARSING_ERROR',
        originalStatus: 400,
        error: `Invalid URL construction: ${(ex as Error).message}`,
        data: JSON.stringify({ baseUrl, path, message: (ex as Error).message })
      };

      return { error };
    }

    const resiliencyStorage = getResiliencyStorage();

    /** Total failure limit check */
    if (maxRequestTotalBreakerEnabled && resiliencyStorage.totalFailures > maxTotal) {
      trackException('Total failure count reached.', maxTotal, urlString);

      const error: FetchBaseQueryError = {
        status: 429,
        data: {
          urlString,
          totalFailures: resiliencyStorage.totalFailures,
          maxTotal,
          message: 'Too many total failed requests'
        }
      };

      return { error };
    }

    /** Per request limit check */
    if (maxRequestBreakerEnabled) {
      const failureCount = resiliencyStorage.failures[urlString]?.count ?? 0;
      if (failureCount > maxPerRequest) {
        trackException('Failure count reached for request url', maxPerRequest, urlString);

        const error: FetchBaseQueryError = {
          status: 429,
          data: {
            urlString,
            failureCount,
            maxPerRequest,
            message: 'Too many failed requests for this URL'
          }
        };

        return { error };
      }
    }

    const result = await baseQuery(...args);

    if (
      (maxRequestTotalBreakerEnabled || maxRequestBreakerEnabled) &&
      errorCodesForRetry.includes(result.error?.status ?? '')
    ) {
      incrementFailure(urlString);
    }

    return result;
  };

  return BaseQueryWithCircuitBreaker;
};
